import { getConfigOptions } from './getConfig'
import { useMutation, useQueryClient, type MutationFunction } from '@tanstack/react-query'
import type { MutationConfig } from '@/shared/utils/reactQuery'
import type { OidcClientConfig } from '../types/authTypes'
import { config as appConfig } from '@/config'

type LogoutCallbackMutationFunctionProps = {
  idToken: string
}

type LogoutRedirectProps = LogoutCallbackMutationFunctionProps & {
  config: OidcClientConfig
}

const logoutRedirect = async ({ config, idToken }: LogoutRedirectProps) => {
  const logoutUrl = new URL(config.end_session_endpoint)
  const logoutParams = new URLSearchParams()
  logoutParams.set('client_id', appConfig.auth.clientId)
  logoutParams.set('post_logout_redirect_uri', appConfig.auth.logoutRedirectUri)
  logoutParams.set('id_token_hint', idToken)
  logoutUrl.search = logoutParams.toString()
  window.location.href = logoutUrl.toString()
}

type UseLogoutCallbackProps = {
  mutationConfig?: MutationConfig<MutationFunction<void, LogoutCallbackMutationFunctionProps>>
}

const useLogoutRedirect = ({ mutationConfig }: UseLogoutCallbackProps = {}) => {
  const { ...restConfig } = mutationConfig || {}
  const queryClient = useQueryClient()

  return useMutation({
    ...restConfig,
    meta: { silent: true },
    mutationFn: async ({ idToken }: LogoutCallbackMutationFunctionProps) => {
      const configQuery = getConfigOptions()
      const config = queryClient.getQueryData(configQuery.queryKey) ?? (await queryClient.fetchQuery(configQuery))
      logoutRedirect({ config, idToken })
    },
  })
}

export { logoutRedirect, useLogoutRedirect }
